import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fetchConfig, formatApiError } from "@/lib/api";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { PlaybookView } from "@/components/PlaybookView";
import { GameDayView } from "@/components/gameday/GameDayView";
import { LineupCard } from "@/components/lineups/LineupCard";
import { Segmented } from "@/components/controls/Segmented";

const TABS = [
  { value: "playbook", label: "Playbook" },
  { value: "lineups", label: "Lineups" },
  { value: "gameday", label: "Game day" },
];

const CodeForm = ({ error }) => {
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  return (
    <form className="card max-w-md mx-auto space-y-4 !p-6" onSubmit={(e) => { e.preventDefault(); navigate(`/c/${code.trim().toUpperCase()}`); }}>
      <h1 className="font-heading text-4xl font-extrabold uppercase">Coach access</h1>
      <p className="text-sm font-medium text-[var(--muted)]">Enter the coach code from your head coach.</p>
      <input data-testid="staff-code-input" className="field-input w-full font-mono text-2xl tracking-[0.3em] uppercase text-center" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
      {error && <p className="text-sm font-bold text-red-600" data-testid="staff-error">{error}</p>}
      <button className="btn-primary w-full" data-testid="staff-code-submit">Open</button>
    </form>
  );
};

export default function StaffPage() {
  const { code } = useParams();
  const [config, setConfig] = useState(null);
  const [team, setTeam] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("playbook");
  const [lineups, setLineups] = useState([]);
  const [gameday, setGameday] = useState(null);

  useEffect(() => { fetchConfig().then(setConfig); }, []);
  useEffect(() => {
    if (!code) return;
    api.staffTeam(code).then(setTeam).catch((e) => setError(e.response?.status === 402 ? formatApiError(e) : "No team found for that coach code."));
    api.staffLineups(code).then(setLineups).catch(() => {});
    api.staffGameday(code).then(setGameday).catch(() => {});
  }, [code]);
  const loadPlays = useCallback((s) => api.staffPlays(code, s), [code]);

  if (!code || error) return <PageShell><AppHeader /><CodeForm error={error} /></PageShell>;
  if (!config || !team) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading...</div>;
  const cfg = config.sports[team.sport][team.level];

  return (
    <PageShell>
      <AppHeader />
      <div className="team-banner" data-testid="staff-banner">
        <div className="min-w-0">
          <p className="ctl-label">Coaches only · Head coach {team.head_coach}</p>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold uppercase tracking-tight truncate" data-testid="staff-team-name">{team.name}</h1>
        </div>
        <Segmented options={TABS} value={tab} onChange={setTab} testPrefix="staff-tab" />
      </div>
      {tab === "playbook" && <PlaybookView cfg={cfg} loadPlays={loadPlays} />}
      {tab === "lineups" && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="staff-lineups">
          {lineups.map((l) => <LineupCard key={l.id} lineup={l} cfg={cfg} teamName={team.name} />)}
          {!lineups.length && <div className="card font-semibold">No lineups shared yet.</div>}
        </div>
      )}
      {tab === "gameday" && gameday && <GameDayView cfg={cfg} plays={gameday.selected} />}
    </PageShell>
  );
}
